import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import ArrowDown from '@/images/ArrowDown.svg';
import Eye from '@/images/Eye.svg';
import EyeClosed from '@/images/EyeClosed.svg';
import Key from '@/images/Key.svg';
import Trash from '@/images/Trash.svg';
import theme from '@/theme';
import styled from '@emotion/styled';
import { CircularProgress, IconButton, InputAdornment, MenuItem } from '@mui/material';
import Select from '@mui/material/Select';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useWebhookComponentContext } from '../../../contex';
import { WebhookFormValues } from '../../../types';
import { FieldLabel } from '../../common';
import { InputField } from '../../common/InputField';

const options = [
    { value: 'basic', label: 'Basic' },
    { value: 'bearer', label: 'Bearer' },
    { value: 'api_key', label: 'API Key' },
    { value: 'none', label: 'None' },
];

const AuthFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const StyledSelect = styled(Select)`
    padding: 0;
    border: 1px solid ${theme.colors.black[300]};
    border-radius: 8px !important;
    color: ${theme.colors.secondary[700]};
    cursor: pointer;

    color: ${theme.colors.secondary.main};

    font-family: Manrope;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: -0.14px;

    :hover {
        border: 1px solid ${theme.colors.secondary[900]};
    }

    fieldset {
        border: none;
    }

    .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input {
        padding: 9px 12px;
    }

    svg {
        right: 8px;
        color: ${theme.colors.secondary.main};
        top: unset;
    }
` as unknown as typeof Select;

const StyledMenuItem = styled(MenuItem)`
    margin: 6px 8px;
    padding: 0 12px;
    border-radius: 8px;
    gap: 6px;
    align-items: center;

    &.Mui-selected,
    &.Mui-selected.Mui-focusVisible {
        background-color: rgba(0, 0, 0, 0.04);

        :hover {
            background-color: rgba(0, 0, 0, 0.04);
        }
    }
`;

interface SecretInputFieldProps {
    placeholder: string;
    name: 'auth.username' | 'auth.password' | 'auth.api_key' | 'auth.token';
    mode: 'edit' | 'create';
    required?: boolean | string;
    error?: boolean;
    helperText?: string;
}

const SecretInputField = ({ placeholder, name, mode, required, error, helperText }: SecretInputFieldProps) => {
    const { watch, setValue, control } = useFormContext<WebhookFormValues>();
    const { isLoadingSecret, secretLoadError } = useWebhookComponentContext();
    const [showSecret, setShowSecret] = useState(false);
    const [wasCleared, setWasCleared] = useState(false);

    const value = watch(name);
    const showEyeIcon = mode === 'create' || wasCleared || !value;
    const displayError = error || !!secretLoadError;
    const displayHelperText = helperText || (secretLoadError ? 'Failed to load secret. Please try again or enter manually.' : undefined);

    const handleShowSecret = () => setShowSecret(show => !show);

    const handleDeleteSecret = () => {
        setValue(name, undefined, { shouldValidate: true });
        setWasCleared(true);
    };

    const handleMouseDownSecret = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={required ? { required: typeof required === 'string' ? required : 'Field is required' } : undefined}
            render={({ field }) => (
                <InputField
                    error={displayError}
                    helperText={displayHelperText}
                    placeholder={placeholder}
                    type={showSecret ? 'text' : 'password'}
                    {...field}
                    value={value || ''} // keep this this way instead of spread field
                    disabled={isLoadingSecret}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <MaskedIcon src={Key} size="16px" color={theme.colors.secondary.main} />
                            </InputAdornment>
                        ),
                        endAdornment: isLoadingSecret ? (
                            <InputAdornment position="end">
                                <CircularProgress size={16} />
                            </InputAdornment>
                        ) : showEyeIcon ? (
                            <InputAdornment position="end">
                                <IconButton onClick={handleShowSecret} onMouseDown={handleMouseDownSecret} edge="end">
                                    <MaskedIcon src={showSecret ? EyeClosed : Eye} size="16px" color={theme.colors.secondary.main} />
                                </IconButton>
                            </InputAdornment>
                        ) : (
                            <InputAdornment position="end">
                                <IconButton onClick={handleDeleteSecret} onMouseDown={handleMouseDownSecret} edge="end">
                                    <MaskedIcon src={Trash} size="16px" color={theme.colors.secondary.main} />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            )}
        />
    );
};

interface Props {
    mode: 'edit' | 'create';
}

export const AuthField = ({ mode }: Props) => {
    const {
        control,
        register,
        formState: { errors },
    } = useFormContext<WebhookFormValues>();

    return (
        <AuthFieldContainer>
            <FieldLabel label="Authentication" />
            <input style={{ visibility: 'hidden', position: 'absolute' }} type="text" {...register('auth.secret_id')} />
            <Controller
                control={control}
                name="auth.type"
                render={({ field }) => (
                    <>
                        <StyledSelect
                            {...field}
                            IconComponent={(p: { className: string }) => <MaskedIcon {...p} src={ArrowDown} size="24px" />}>
                            {options.map(option => (
                                <StyledMenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </StyledMenuItem>
                            ))}
                        </StyledSelect>

                        {field.value === 'basic' && (
                            <>
                                <FieldLabel label="Username" />
                                <InputField
                                    placeholder="Username"
                                    inputProps={{ ...register('auth.username', { required: 'Enter username' }) }}
                                    error={!!errors.auth?.username}
                                    helperText={errors.auth?.username?.message}
                                />

                                <FieldLabel label="Password" />
                                <SecretInputField
                                    placeholder="Password"
                                    name="auth.password"
                                    mode={mode}
                                    error={!!errors.auth?.password}
                                    helperText={errors.auth?.password?.message}
                                    required="Enter password"
                                />
                            </>
                        )}
                        {field.value === 'api_key' && (
                            <>
                                <FieldLabel label="Header Name" />
                                <InputField
                                    placeholder="Header Name"
                                    inputProps={{ ...register('auth.header_name', { required: 'Enter header name' }) }}
                                    error={!!errors.auth?.header_name}
                                    helperText={errors.auth?.header_name?.message}
                                />

                                <FieldLabel label="API Key" />
                                <SecretInputField
                                    placeholder="API Key"
                                    name="auth.api_key"
                                    mode={mode}
                                    error={!!errors.auth?.api_key}
                                    helperText={errors.auth?.api_key?.message}
                                    required="Enter an API key"
                                />
                            </>
                        )}
                        {field.value === 'bearer' && (
                            <>
                                <FieldLabel label="Token" />
                                <SecretInputField
                                    placeholder="Token"
                                    name="auth.token"
                                    mode={mode}
                                    error={!!errors.auth?.token}
                                    helperText={errors.auth?.token?.message}
                                    required="Enter token"
                                />
                            </>
                        )}
                    </>
                )}
            />
        </AuthFieldContainer>
    );
};
